import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import type { ThemeDefinition, ThemeId } from '@atlas/shared';
import { DEFAULT_THEME_ID, isThemeId } from '@atlas/shared';
import { useTranslation } from 'react-i18next';
import { getMuiLocale } from '../i18n/muiLocale';
import { createAppTheme, tokensToCssVars, withColorVision, isColorVisionMode } from '@atlas/ui';
import type { ColorVisionMode } from '@atlas/ui';
import { listThemes, themes } from '@atlas/ui';
import { useAuth } from './AuthContext';
import { users as usersApi } from '../services/api';

interface ThemeContextValue {
  themeId: ThemeId;
  setThemeId: (id: ThemeId) => void;
  availableThemes: ThemeDefinition[];
  colorVision: ColorVisionMode;
  setColorVision: (mode: ColorVisionMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'atlas-theme-id';
const LEGACY_MODE_KEY = 'atlas-theme-mode';
// ponytail: per-device only; sync via user preferences like themeId if people ask.
const COLOR_VISION_KEY = 'atlas-color-vision';

function readColorVision(): ColorVisionMode {
  try {
    const stored = window.localStorage.getItem(COLOR_VISION_KEY);
    return isColorVisionMode(stored) ? stored : 'standard';
  } catch {
    return 'standard';
  }
}

/** Read the cached theme id from localStorage with one-shot legacy migration. */
function readCachedThemeId(): ThemeId {
  if (typeof window === 'undefined') return DEFAULT_THEME_ID;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isThemeId(stored)) return stored;

  // Migrate from the previous light/dark toggle key, if present.
  const legacy = window.localStorage.getItem(LEGACY_MODE_KEY);
  if (legacy === 'light') {
    window.localStorage.setItem(STORAGE_KEY, 'classic-light');
    window.localStorage.removeItem(LEGACY_MODE_KEY);
    return 'classic-light';
  }
  if (legacy === 'dark') {
    window.localStorage.setItem(STORAGE_KEY, DEFAULT_THEME_ID);
    window.localStorage.removeItem(LEGACY_MODE_KEY);
    return DEFAULT_THEME_ID;
  }

  return DEFAULT_THEME_ID;
}

function applyCssVariables(def: ThemeDefinition) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const vars = tokensToCssVars(def);
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  root.dataset.theme = def.id;
  root.style.colorScheme = def.paletteMode;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeId, setThemeIdState] = useState<ThemeId>(readCachedThemeId);
  const [colorVision, setColorVisionState] = useState<ColorVisionMode>(readColorVision);
  const activeTheme = useMemo(() => withColorVision(themes[themeId], colorVision), [themeId, colorVision]);

  // Apply CSS variables synchronously when the id changes — keeps non-MUI
  // styled code (markdown blocks, custom sx referencing var(--token)) in sync.
  useEffect(() => {
    applyCssVariables(activeTheme);
    window.localStorage.setItem(STORAGE_KEY, themeId);
  }, [activeTheme, themeId]);

  const setColorVision = useCallback((mode: ColorVisionMode) => {
    setColorVisionState(mode);
    try {
      window.localStorage.setItem(COLOR_VISION_KEY, mode);
    } catch {
      // Storage blocked: the choice still applies for this session.
    }
  }, []);

  const setThemeId = useCallback((next: ThemeId) => {
    setThemeIdState((prev) => {
      if (prev === next) return prev;
      // Fire-and-forget; failure handling lives in ThemeSync via the same hook.
      usersApi.setTheme(next).catch((err) => {
        console.warn('Failed to persist theme selection', err);
      });
      return next;
    });
  }, []);

  const { i18n } = useTranslation();
  const muiTheme = useMemo(
    () => createAppTheme(activeTheme, getMuiLocale(i18n.language)),
    [activeTheme, i18n.language],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      themeId,
      setThemeId,
      availableThemes: listThemes(),
      colorVision,
      setColorVision,
    }),
    [themeId, setThemeId, colorVision, setColorVision],
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

/**
 * Lives inside the auth tree. On first authenticated render, pulls the server
 * value of preferences.themeId and reconciles with the local cache:
 *  - server value present  → adopt it (covers cross-device sync)
 *  - server value null     → push the local cache up (one-time backfill)
 * Subsequent auth changes (e.g. logout/login) re-run reconciliation.
 */
export function ThemeSync() {
  const { user } = useAuth();
  const { themeId, setThemeId } = useTheme();
  const syncedUserRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUser = user?.userId ?? null;
    if (!currentUser) {
      syncedUserRef.current = null;
      return;
    }
    if (syncedUserRef.current === currentUser) return;
    syncedUserRef.current = currentUser;

    let cancelled = false;
    (async () => {
      try {
        const result = await usersApi.getTheme();
        if (cancelled) return;
        if (result.themeId && isThemeId(result.themeId)) {
          if (result.themeId !== themeId) {
            // Direct state update, not setThemeId — we don't want to PUT what
            // the server just told us.
            window.localStorage.setItem(STORAGE_KEY, result.themeId);
            // Trigger a re-render by going through setThemeId; the PUT is idempotent.
            setThemeId(result.themeId);
          }
        } else {
          // No server value yet → backfill with whatever the user has locally.
          await usersApi.setTheme(themeId);
        }
      } catch (err) {
        console.warn('Theme sync failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, themeId, setThemeId]);

  return null;
}
